/**
 * push.js — Web Push utility for Karmana Journeys.
 *
 * Usage:
 *   import { registerSW, subscribeToPush, isStandalone } from '../utils/push';
 *   await registerSW();
 *   await subscribeToPush();
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

/** Convert a URL-safe base64 string to Uint8Array for VAPID subscription. */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/** True if the app is running in standalone (installed PWA) mode. */
export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

/** True if the current browser is iOS Safari. */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/** Register the service worker. Safe to call multiple times. */
export async function registerSW() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    return reg;
  } catch (err) {
    console.warn('[push] SW registration failed:', err);
    return null;
  }
}

/** Request notification permission and subscribe to push. Saves subscription to backend. */
export async function subscribeToPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('Push notifications are not supported in this browser.');
  }
  if (!VAPID_PUBLIC_KEY) {
    throw new Error('VAPID public key not configured.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }

  const reg = await navigator.serviceWorker.ready;
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const subJSON = subscription.toJSON();
  const res = await fetch(`${API_URL}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ endpoint: subJSON.endpoint, keys: subJSON.keys }),
  });
  if (!res.ok) throw new Error('Failed to save push subscription on server.');
  return subscription;
}

/** Mark in localStorage that the notification prompt has been shown. */
export function markNotifPrompted() {
  try { localStorage.setItem('karmana_notif_prompted', '1'); } catch (_) {}
}

/** Returns true if the notification prompt has NOT been shown yet. */
export function shouldShowNotifPrompt() {
  if (!('Notification' in window)) return false;
  if (Notification.permission !== 'default') return false;
  try { return !localStorage.getItem('karmana_notif_prompted'); } catch (_) { return false; }
}
